/* eslint-disable */
import { useEffect, useState } from "react";
import { X, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const REQUIRED_PERMISSIONS = {
  "orders.create": ["orders.view", "products.view"],
  "orders.update": ["orders.view"],
  "orders.delete": ["orders.view"],
  "products.create": ["products.view"],
  "products.update": ["products.view"],
  "products.delete": ["products.view"],
  "workers.create": ["workers.view"],
  "workers.update": ["workers.view"],
  "workers.delete": ["workers.view"],
  "workers.manage_permissions": ["workers.view"],
  "store.update": ["store.view"],
};

const truncate = (str, max = 25) =>
  str && str.length > max ? str.slice(0, max) + "…" : str;

const PermissionsModal = ({
  isOpen,
  onClose,
  user,
  products = [],
  categories = [],
  getRoleInfo,
  getPermissionsByCategory,
  onTogglePermission,
  onSavePermissions,
  onUpdateProducts,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [saving, setSaving] = useState(false);
  const [localPermissions, setLocalPermissions] = useState(
    user?.permissions || [],
  );
  const [localProducts, setLocalProducts] = useState(user?.products || []);
  const isAllProductsSelected =
    products.length > 0 &&
    localProducts.length === products.length &&
    products.every((product) => localProducts.includes(product.id));

  useEffect(() => {
    if (user) {
      setLocalPermissions(user.permissions || []);
      setLocalProducts(user.products || []);
    }
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  const handleTogglePermission = (permissionId) => {
    if (saving) return;

    const isCurrentlyActive = localPermissions.includes(permissionId);

    if (!isCurrentlyActive) {
      const permissionsToAdd = [permissionId];
      const requiredPerms = REQUIRED_PERMISSIONS[permissionId] || [];

      requiredPerms.forEach((requiredPerm) => {
        if (!localPermissions.includes(requiredPerm)) {
          permissionsToAdd.push(requiredPerm);
        }
      });

      setLocalPermissions([...localPermissions, ...permissionsToAdd]);
      permissionsToAdd.forEach((perm) => onTogglePermission(perm));
    } else {
      setLocalPermissions(localPermissions.filter((p) => p !== permissionId));
      onTogglePermission(permissionId);
    }
  };

  // ✅ FIX 1: بعت localPermissions و localProducts للـ parent
  const handleSave = async () => {
    setSaving(true);
    const validProducts = localProducts.filter((id) => id !== "");
    await onSavePermissions(localPermissions, validProducts);
    setSaving(false);
  };

  const addProductRow = () => {
    if (saving) return;
    // ما نزيدش row جديد كي الآخر لازال فارغ
    if (
      localProducts.length > 0 &&
      localProducts[localProducts.length - 1] === ""
    )
      return;
    const next = [...localProducts, ""];
    setLocalProducts(next);
    onUpdateProducts?.(next);
  };

  const updateProductAt = (index, value) => {
    const next = [...localProducts];
    next[index] = value;
    setLocalProducts(next);
    onUpdateProducts?.(next);
  };

  const removeProductAt = (index) => {
    const next = localProducts.filter((_, i) => i !== index);
    setLocalProducts(next);
    onUpdateProducts?.(next);
  };

  const toggleSelectAllProducts = (checked) => {
    if (saving) return;
    const next = checked ? products.map((product) => product.id) : [];
    setLocalProducts(next);
    onUpdateProducts?.(next);
  };

  const isOptionDisabled = (productId, rowIndex) => {
    return localProducts.some((id, i) => i !== rowIndex && id === productId);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col border border-[#fbf4ff] dark:!border-[#3c3a3d]">
        {/* Header */}
        <div className="p-6 border-b border-[#fbf4ff] dark:!border-[#3c3a3d] bg-[#fcf6ff] dark:!bg-[#1e1e1e]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#9e68ba] mb-3">
                {t("workersManagement.permissionsModal.title")}
              </h2>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center text-white font-semibold shadow-sm">
                  {user.email?.charAt(0) ||
                    t("workersManagement.permissionsModal.userInitialFallback")}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-200">
                    {user.name}
                  </p>
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getRoleInfo(user.role).color}`}
                  >
                    {getRoleInfo(user.role).label}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-gray-400 hover:text-[#9e68ba] transition-colors p-1 rounded-full hover:bg-[#faf1ff] dark:hover:bg-[#2a2a2a]"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Permissions content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:!bg-[#181819]">
          <div className="space-y-4">
            {categories.map((category) => {
              const categoryPermissions = getPermissionsByCategory(category.id);
              const activeCount = categoryPermissions.filter((p) =>
                localPermissions.includes(p.id),
              ).length;

              return (
                <div
                  key={category.id}
                  className="border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl p-4 bg-white dark:!bg-[#181819] shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-[#9e68ba]">
                      {category.label}
                    </h3>
                    <span className="text-sm px-3 py-1 bg-[#faf1ff] dark:!bg-[#1e1e1e] text-[#9e68ba] rounded-full font-medium">
                      {activeCount} / {categoryPermissions.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {categoryPermissions.map((permission) => {
                      const isActive = localPermissions.includes(permission.id);
                      return (
                        <div
                          key={permission.id}
                          className="flex items-center justify-between p-3 bg-[#fcf6ff] dark:!bg-[#1e1e1e] dark:!border-[#3c3a3d] rounded-lg hover:bg-[#faf1ff] dark:hover:bg-[#2a2a2a] transition-colors border border-transparent hover:border-[#fbf4ff] dark:hover:border-[#3c3a3d]"
                        >
                          <span className="text-gray-700 dark:text-gray-200 font-medium">
                            {permission.label}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handleTogglePermission(permission.id)
                            }
                            disabled={saving}
                            className={`w-12 h-6 rounded-full transition-all ${
                              isActive
                                ? "bg-[#9b59bf]"
                                : "bg-gray-300 dark:bg-gray-600"
                            } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                          >
                            <div
                              className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform flex items-center justify-center ${
                                isActive ? "translate-x-6" : "translate-x-0.5"
                              }`}
                            >
                              {isActive ? (
                                <Check className="w-3 h-3 text-[#9b59bf]" />
                              ) : (
                                <X className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {user.role === "confirmer" && (
              <div className="border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl p-4 bg-white dark:!bg-[#181819] shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-[#9e68ba]">
                    {t("workersManagement.permissionsModal.assignedProducts")}
                  </h3>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={isAllProductsSelected}
                        onChange={(e) => toggleSelectAllProducts(e.target.checked)}
                        disabled={saving || products.length === 0}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 dark:!border-gray-700 dark:!bg-[#1e1e1e]"
                      />
                      {t("workersManagement.permissionsModal.selectAllProducts")}
                    </label>
                    <button
                      type="button"
                      onClick={addProductRow}
                      disabled={saving || products.length === 0 || isAllProductsSelected}
                      className="px-3 py-1.5 text-xs rounded-lg bg-[#9b59bf] hover:bg-[#814a9f] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("workersManagement.permissionsModal.addProduct")}
                    </button>
                  </div>
                </div>

                {localProducts.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t("workersManagement.permissionsModal.noAssignedProducts")}
                  </p>
                )}

                <div className="space-y-2">
                  {localProducts.map((productId, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select
                        value={productId}
                        onChange={(e) => updateProductAt(idx, e.target.value)}
                        className="w-0 min-w-0 flex-1 px-3 py-2 bg-gray-50 dark:bg-[#1e1e1e] border border-gray-200 dark:!border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white truncate"
                        disabled={saving}
                      >
                        <option value="" className="dark:bg-[#1e1e1e]">
                          {t(
                            "workersManagement.permissionsModal.selectProduct",
                          )}
                        </option>
                        {products.map((p) => (
                          <option
                            key={p.id}
                            value={p.id}
                            disabled={isOptionDisabled(p.id, idx)}
                            className="dark:bg-[#1e1e1e]"
                          >
                            {truncate(p?.name) ||
                              t("workersManagement.table.noNameProduct")}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeProductAt(idx)}
                        disabled={saving}
                        className="flex-shrink-0 px-3 py-2 text-xs rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
                      >
                        {t("workersManagement.permissionsModal.delete")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#fbf4ff] dark:!border-[#3c3a3d] bg-[#fcf6ff] dark:!bg-[#1e1e1e]">
          <div className="mb-4 text-center">
            <div className="inline-flex items-center gap-2 bg-white dark:!bg-[#181819] px-4 py-2 rounded-lg border border-[#fbf4ff] dark:!border-[#3c3a3d]">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("workersManagement.permissionsModal.totalEnabled")}
              </p>
              <span className="font-bold text-[#9e68ba] text-lg">
                {localPermissions.length || 0}
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-[#9b59bf] hover:bg-[#814a9f] text-white px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving
                ? t("workersManagement.permissionsModal.saving")
                : t("workersManagement.permissionsModal.saveChanges")}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 bg-gray-200 dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg hover:bg-gray-300 dark:hover:bg-[#3a3a3a] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("workersManagement.permissionsModal.cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModal;
